// The contents' icons (the sidebar redesign, `design-docs/Sidebar redesign — design.md`): line drawings on a 24-unit
// grid, stroked in the current colour, and the tile they sit on — a rounded square washed with an ink. An ink is a class
// (`tint-<ink>`: the InkPalette tokens, or `tint-accent` for terracotta) or, for a label's own colour, `--tint` set
// through the CSSOM (the CSP allows no style attributes).

/** Each icon's path ("M… M…": several strokes in one). */
const PATHS = {
  // Arrangements
  book: 'M5 4h10a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3z M5 17a3 3 0 0 1 3-3h10',
  notes: 'M5 5h14 M5 10h14 M5 15h9 M5 20h6',
  send: 'M4 12l16-8-6 16-3-7z M11 13l9-9',
  fork: 'M7 4v8a4 4 0 0 0 4 4h6 M7 4a2 2 0 1 0 0 .1 M17 16a2 2 0 1 0 0 .1 M17 4v12',
  trash: 'M5 7h14 M10 7V4h4v3 M7 7l1 13h8l1-13',
  // Collections
  stack: 'M4 8l8-4 8 4-8 4z M4 12l8 4 8-4 M4 16l8 4 8-4',
  envelope: 'M3 6h18v12H3z M3 6l9 7 9-7',
  fog: 'M3 9h13 M6 13h15 M3 17h11 M8 5h8',
  person: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M4 21a8 8 0 0 1 16 0',
  eye: 'M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  key: 'M8 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M12 12h9 M17 12v3 M20 12v2',
  flag: 'M5 21V4 M5 4h11l-2 4 2 4H5',
  // Facets
  tag: 'M3 12V3h9l9 9-9 9z M7.5 7.5h.01',
  number: 'M9 4L7 20 M17 4l-2 16 M4 9h16 M3 15h16',
  mappin: 'M12 21s-7-6.5-7-12a7 7 0 0 1 14 0c0 5.5-7 12-7 12z M12 11.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  list: 'M9 6h11 M9 12h11 M9 18h11 M4.5 6h.01 M4.5 12h.01 M4.5 18h.01',
  text: 'M5 7V5h14v2 M12 5v14 M9 19h6',
  heading: 'M6 4v16 M18 4v16 M6 12h12',
  'text-align': 'M4 6h16 M4 10h10 M4 14h16 M4 18h10',
  note: 'M5 4h14v11l-5 5H5z M14 20v-5h5',
  calendar: 'M4 6h16v14H4z M4 10h16 M8 3v4 M16 3v4',
  target: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M12 12h.01',
  checkmark: 'M4 4h16v16H4z M8 12l3 3 5-6',
  lock: 'M6 11h12v10H6z M8.5 11V8a3.5 3.5 0 0 1 7 0v3',
  doc: 'M7 3h7l4 4v14H7z M14 3v4h4 M10 12h5 M10 16h4',
  folder: 'M3 6h6l2 2h10v11H3z',
  bubble: 'M4 5h16v11H9l-5 4z',
  link: 'M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1 M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1',
  // Kinds
  script: 'M7 3h10v18H7z M10 8h4 M9 12h6 M10 16h4',
  research: 'M6 3h9l3 3v15H6z M10 13a2.5 2.5 0 1 0 0-.1 M12 15l2.5 2.5',
  // Controls
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z M20 20l-4-4',
  filter: 'M4 6h16 M7 12h10 M10 18h4',
  chevron: 'M7 10l5 5 5-5',
  check: 'M5 12l5 5 9-10',
  close: 'M6 6l12 12 M18 6L6 18',
  pencil: 'M4 20h4L19 9l-4-4L4 16z',
  region: 'M4 9a5 5 0 0 1 5-5h6a5 5 0 0 1 5 5v6a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5z M8.5 10h4 M11.5 14h4',
  plus: 'M12 5v14 M5 12h14',
  rows: 'M4 6h16 M4 12h16 M4 18h16',
  all: 'M4 4h7v7H4z M13 4h7v7h-7z M4 13h7v7H4z M13 13h7v7h-7z',
  gear:
    'M9.87 5.12L10.49 2.62L13.51 2.62L14.13 5.12L15.36 5.63L17.56 4.3L19.7 6.44L18.37 8.64L18.88 9.87L21.38 10.49L21.38 13.51L18.88 14.13L18.37 15.36L19.7 17.56L17.56 19.7L15.36 18.37L14.13 18.88L13.51 21.38L10.49 21.38L9.87 18.88L8.64 18.37L6.44 19.7L4.3 17.56L5.63 15.36L5.12 14.13L2.62 13.51L2.62 10.49L5.12 9.87L5.63 8.64L4.3 6.44L6.44 4.3L8.64 5.63z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
} as const;

export type IconName = keyof typeof PATHS;

/** The InkPalette inks a tile or icon can wear, and the accent (terracotta). */
export type Ink = 'red' | 'orange' | 'yellow' | 'green' | 'blue' | 'pink' | 'purple' | 'grey' | 'accent';

export function Icon({ name, size = 14, className }: { name: IconName; size?: number; className?: string }) {
  return (
    <svg
      className={className ? `icon ${className}` : 'icon'}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}

/** An icon on a rounded square washed with an ink (16%), the icon in the ink; or with a colour of its own (`color`, a
 *  CSS colour: a label's). `size` is the tile's: 20, 22, 24, 26, 28, 30 or 46. */
export function Tile({ icon, ink, color, size = 24, className = '' }: { icon: IconName; ink?: Ink; color?: string | null; size?: number; className?: string }) {
  const own = color !== undefined;
  return (
    <span
      className={`tile tile-${size}${own ? '' : ` tint-${ink ?? 'grey'}`}${own && !color ? ' tint-grey' : ''}${className ? ` ${className}` : ''}`}
      aria-hidden="true"
      ref={own ? (el) => void (color ? el?.style.setProperty('--tint', color) : el?.style.removeProperty('--tint')) : undefined}
    >
      <Icon name={icon} size={Math.round(size * (size >= 40 ? 0.48 : 0.56))} />
    </span>
  );
}
